import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Bandaids,
  Knife,
  MapPinLine,
  Sparkle,
  Stethoscope,
  Copy,
  FloppyDisk,
  Trash,
  PencilSimple,
  Ruler,
} from "@phosphor-icons/react";
import Sidebar from "@/components/Sidebar";
import CodeColumn from "@/components/CodeColumn";
import HighlightedNote from "@/components/HighlightedNote";
import IssSummary from "@/components/IssSummary";
import ValidationBanner from "@/components/ValidationBanner";
import { computeIss } from "@/lib/iss";
import { toggleCodeOverride, toggleAisOverride } from "@/lib/override";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyResult = { injuries: [], procedures: [], external_causes: [], summary: null };

export default function TraumaCoder() {
  const [note, setNote] = useState("");
  const [result, setResult] = useState(emptyResult);
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState(null);
  const [cases, setCases] = useState([]);
  const [activeCaseId, setActiveCaseId] = useState(null);
  const [accepted, setAccepted] = useState({ injuries: {}, procedures: {}, external_causes: {}, ais: {} });
  const [rejected, setRejected] = useState({ injuries: {}, procedures: {}, external_causes: {}, ais: {} });
  const [hoverEvidence, setHoverEvidence] = useState("");
  const [editMode, setEditMode] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [stats, setStats] = useState(null);
  const textareaRef = useRef(null);

  const issStats = useMemo(() => computeIss(result.injuries || []), [result.injuries]);
  const aisInjuries = useMemo(
    () => (result.injuries || []).filter((i) => i.ais2015_code && i.ais_severity),
    [result.injuries]
  );

  const favoritesIndex = useMemo(() => {
    const idx = {};
    (favorites || []).forEach((f) => {
      const key = `${f.kind}:${f.kind === "ais" ? f.ais2015_code || f.code : f.code}`;
      idx[key] = f;
    });
    return idx;
  }, [favorites]);

  const fetchCases = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/cases`);
      setCases(data);
    } catch (e) {
      console.error("fetch cases failed", e);
    }
  }, []);

  const fetchFavorites = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/favorites`);
      setFavorites(data);
    } catch (e) {
      console.error("fetch favorites failed", e);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/stats/corrections`);
      setStats(data);
    } catch (e) {
      console.error("fetch stats failed", e);
    }
  }, []);

  useEffect(() => {
    fetchCases();
    fetchFavorites();
    fetchStats();
  }, [fetchCases, fetchFavorites, fetchStats]);

  const handleToggleOverride = (group, item, isAis) =>
    setResult((r) => {
      const list = [...(r[group] || [])];
      const idx = list.indexOf(item);
      if (idx < 0) return r;
      list[idx] = isAis ? toggleAisOverride(item, r.draft) : toggleCodeOverride(item, group, r.draft);
      return { ...r, [group]: list };
    });

  const handleToggleFavorite = async (item, kind) => {
    const code = kind === "ais" ? item.ais2015_code || item.code : item.code;
    const key = `${kind}:${code}`;
    const existing = favoritesIndex[key];
    if (existing) {
      try {
        await axios.delete(`${API}/favorites/${existing.id}`);
        setFavorites((f) => f.filter((x) => x.id !== existing.id));
        toast.success(`Removed ${code} from favorites`);
      } catch {
        toast.error("Remove favorite failed");
      }
      return;
    }
    const payload = {
      kind,
      code,
      description:
        kind === "ais"
          ? item.ais2015_description || item.description
          : item.description,
      laterality: item.laterality || null,
      encounter: item.encounter || null,
      approach: item.approach || null,
      body_system: item.body_system || null,
      category: item.category || null,
      ais2015_code: item.ais2015_code || null,
      ais2015_description: item.ais2015_description || null,
      ais_region: item.ais_region || null,
      ais_severity: item.ais_severity || null,
    };
    try {
      const { data } = await axios.post(`${API}/favorites`, payload);
      setFavorites((f) => {
        if (f.some((x) => x.id === data.id)) return f;
        return [data, ...f];
      });
      toast.success(`Saved ${code} to favorites`);
    } catch {
      toast.error("Save favorite failed");
    }
  };

  const handleRemoveFavorite = async (fav) => {
    try {
      await axios.delete(`${API}/favorites/${fav.id}`);
      setFavorites((f) => f.filter((x) => x.id !== fav.id));
      toast.success("Favorite removed");
    } catch {
      toast.error("Remove failed");
    }
  };

  const resetDecisions = () =>
    setAccepted({ injuries: {}, procedures: {}, external_causes: {}, ais: {} }) ||
    setRejected({ injuries: {}, procedures: {}, external_causes: {}, ais: {} });

  const handleAnalyze = async () => {
    const trimmed = note.trim();
    if (trimmed.length < 10) {
      toast.error("Paste a clinical note first (min 10 characters).");
      return;
    }
    setLoading(true);
    setResult(emptyResult);
    resetDecisions();
    try {
      setStage("drafting");
      const { data: draft } = await axios.post(`${API}/analyze`, { note: trimmed });
      setStage("validating");
      const { data } = await axios.post(`${API}/validate`, { note: trimmed, draft });
      setResult(data);
      setEditMode(false);
      const total = data.injuries.length + data.procedures.length + data.external_causes.length;
      const corrected = [...data.injuries, ...data.procedures, ...data.external_causes].filter(
        (c) => c.corrected_from || c.ais_corrected_from
      ).length;
      toast.success(
        `Validated ${total} codes · ${corrected} corrected · ${data.removed?.length || 0} removed`
      );
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Analysis failed";
      toast.error(msg);
      setResult(emptyResult);
    } finally {
      setLoading(false);
      setStage(null);
    }
  };

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      handleAnalyze();
    }
    if (e.key === "Escape") {
      setNote("");
      setResult(emptyResult);
      resetDecisions();
      setActiveCaseId(null);
      setEditMode(true);
    }
  };

  const handleLoadSample = (sample) => {
    setNote(sample.text);
    setResult(emptyResult);
    resetDecisions();
    setActiveCaseId(null);
    setEditMode(true);
    textareaRef.current?.focus();
  };

  const handleNewCase = () => {
    setNote("");
    setResult(emptyResult);
    resetDecisions();
    setActiveCaseId(null);
    setEditMode(true);
    textareaRef.current?.focus();
  };

  const handleLoadCase = async (id) => {
    try {
      const { data } = await axios.get(`${API}/cases/${id}`);
      setNote(data.note);
      setResult(data.result || emptyResult);
      setActiveCaseId(id);
      setEditMode(false);
      resetDecisions();
    } catch (e) {
      toast.error("Could not load case");
    }
  };

  const handleDeleteCase = async (id) => {
    try {
      await axios.delete(`${API}/cases/${id}`);
      toast.success("Case deleted");
      if (activeCaseId === id) handleNewCase();
      fetchCases();
      fetchStats();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleSaveCase = async () => {
    if (!note.trim()) {
      toast.error("Nothing to save");
      return;
    }
    const totalCodes =
      (result.injuries?.length || 0) +
      (result.procedures?.length || 0) +
      (result.external_causes?.length || 0);
    if (totalCodes === 0) {
      toast.error("Run Analyze before saving a case");
      return;
    }
    try {
      const { data } = await axios.post(`${API}/cases`, {
        note: note.trim(),
        result,
      });
      toast.success("Case saved");
      setActiveCaseId(data.id);
      fetchCases();
      fetchStats();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const handleCopyAll = async () => {
    const lines = [];
    if (result.injuries?.length) {
      lines.push("ICD-10-CM INJURIES");
      result.injuries.forEach((i) => lines.push(`${i.code}  ${i.description}`));
      lines.push("");
    }
    if (result.procedures?.length) {
      lines.push("ICD-10-PCS PROCEDURES");
      result.procedures.forEach((i) => lines.push(`${i.code}  ${i.description}`));
      lines.push("");
    }
    if (result.external_causes?.length) {
      lines.push("EXTERNAL CAUSES");
      result.external_causes.forEach((i) => lines.push(`${i.code}  ${i.description}`));
      lines.push("");
    }
    const aisInjuries = (result.injuries || []).filter((i) => i.ais2015_code);
    if (aisInjuries.length) {
      lines.push("AIS 2015 (AAAM)");
      aisInjuries.forEach((i) =>
        lines.push(`${i.ais2015_code}  ${i.ais2015_description || i.description}`)
      );
    }
    if (lines.length === 0) {
      toast.error("No codes to copy");
      return;
    }
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      toast.success("All codes copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  const handleExportCsv = () => {
    const rows = [["category", "code", "description", "confidence", "evidence"]];
    (result.injuries || []).forEach((i) =>
      rows.push(["injury", i.code, i.description, i.confidence || "", (i.evidence || "").replace(/\n/g, " ")])
    );
    (result.procedures || []).forEach((i) =>
      rows.push(["procedure", i.code, i.description, i.confidence || "", (i.evidence || "").replace(/\n/g, " ")])
    );
    (result.external_causes || []).forEach((i) =>
      rows.push([
        i.category || "external",
        i.code,
        i.description,
        i.confidence || "",
        (i.evidence || "").replace(/\n/g, " "),
      ])
    );
    (result.injuries || [])
      .filter((i) => i.ais2015_code)
      .forEach((i) =>
        rows.push([
          "ais2015",
          i.ais2015_code,
          i.ais2015_description || i.description,
          i.confidence || "",
          (i.evidence || "").replace(/\n/g, " "),
        ])
      );
    if (rows.length === 1) {
      toast.error("No codes to export");
      return;
    }
    const csv = rows
      .map((r) => r.map((f) => `"${String(f).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `trauma-codes-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };

  const toggleAccept = (group, idx) => {
    setAccepted((a) => ({ ...a, [group]: { ...a[group], [idx]: !a[group][idx] } }));
    setRejected((r) => ({ ...r, [group]: { ...r[group], [idx]: false } }));
  };
  const toggleReject = (group, idx) => {
    setRejected((r) => ({ ...r, [group]: { ...r[group], [idx]: !r[group][idx] } }));
    setAccepted((a) => ({ ...a, [group]: { ...a[group], [idx]: false } }));
  };

  const totalCodes =
    (result.injuries?.length || 0) +
    (result.procedures?.length || 0) +
    (result.external_causes?.length || 0);

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-[#FCFBF9]" data-testid="trauma-coder-root">
      <Sidebar
        cases={cases}
        favorites={favorites}
        onLoadCase={handleLoadCase}
        onDeleteCase={handleDeleteCase}
        onLoadSample={handleLoadSample}
        onNewCase={handleNewCase}
        onRemoveFavorite={handleRemoveFavorite}
        activeCaseId={activeCaseId}
        stats={stats}
      />

      <main className="flex-1 h-full flex flex-col p-4 gap-4 min-w-0">
        {/* Top bar */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl font-semibold tracking-tight text-[#2C332A] leading-none">
              Trauma coding workspace
            </h1>
            <p className="text-xs uppercase tracking-[0.18em] text-[#5C665A] mt-2">
              Paste note · Analyze · Review · Export
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopyAll}
              disabled={totalCodes === 0}
              className="flex items-center gap-1.5 text-sm text-[#2C332A] bg-white border border-[#E5E0D8] hover:bg-[#F4F1EB] disabled:opacity-40 disabled:cursor-not-allowed px-3 py-2 rounded-md transition-colors"
              data-testid="copy-all-button"
            >
              <Copy size={14} /> Copy all
            </button>
            <button
              onClick={handleExportCsv}
              disabled={totalCodes === 0}
              className="flex items-center gap-1.5 text-sm text-[#2C332A] bg-white border border-[#E5E0D8] hover:bg-[#F4F1EB] disabled:opacity-40 disabled:cursor-not-allowed px-3 py-2 rounded-md transition-colors"
              data-testid="export-csv-button"
            >
              Export CSV
            </button>
            <button
              onClick={handleSaveCase}
              disabled={totalCodes === 0}
              className="flex items-center gap-1.5 text-sm text-[#2C332A] bg-white border border-[#E5E0D8] hover:bg-[#F4F1EB] disabled:opacity-40 disabled:cursor-not-allowed px-3 py-2 rounded-md transition-colors"
              data-testid="save-case-button"
            >
              <FloppyDisk size={14} /> Save case
            </button>
          </div>
        </div>

        {/* Bento grid */}
        <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-4 min-h-0">
          {/* Clinical note panel */}
          <section className="md:col-span-3 h-full flex flex-col bg-white border border-[#E5E0D8] rounded-md overflow-hidden shadow-sm min-h-0">
            <header className="flex items-center justify-between px-4 py-3 border-b border-[#E5E0D8]">
              <div className="flex items-center gap-2 min-w-0">
                <Stethoscope size={18} weight="duotone" className="text-[#8C3A27] shrink-0" />
                <div className="min-w-0">
                  <h2 className="font-serif text-lg leading-none tracking-tight text-[#2C332A]">
                    Clinical note
                  </h2>
                  <p className="text-[10px] uppercase tracking-[0.14em] text-[#5C665A] mt-1 truncate">
                    {editMode ? "Editing · paste note here" : "Hover a code card to highlight evidence"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {note.length > 0 && (
                  <span className="text-[10px] text-[#5C665A] font-mono">
                    {note.length.toLocaleString()} ch
                  </span>
                )}
                {!editMode && note && (
                  <button
                    onClick={() => setEditMode(true)}
                    className="flex items-center gap-1 text-[11px] text-[#5C665A] hover:text-[#2C332A] border border-[#E5E0D8] hover:bg-[#F4F1EB] px-2 py-1 rounded transition-colors"
                    data-testid="edit-note-button"
                    title="Edit note"
                  >
                    <PencilSimple size={12} /> Edit
                  </button>
                )}
              </div>
            </header>
            {editMode ? (
              <textarea
                ref={textareaRef}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Paste a trauma clinical note, operative report, or ED note here…

Tip: ⌘/Ctrl + Enter to analyze · Esc to clear"
                className="w-full flex-1 p-4 bg-transparent resize-none outline-none text-sm leading-relaxed text-[#2C332A] placeholder-[#5C665A]/70 tc-scroll"
                data-testid="clinical-note-textarea"
              />
            ) : (
              <div className="flex-1 min-h-0">
                <HighlightedNote note={note} highlight={hoverEvidence} />
              </div>
            )}
            <footer className="flex items-center justify-between gap-2 px-3 py-3 border-t border-[#E5E0D8] bg-[#FCFBF9]">
              <button
                onClick={() => {
                  setNote("");
                  setResult(emptyResult);
                  resetDecisions();
                  setEditMode(true);
                }}
                className="flex items-center gap-1.5 text-sm text-[#5C665A] hover:text-[#2C332A] px-2 py-1.5 rounded transition-colors"
                data-testid="clear-note-button"
              >
                <Trash size={14} /> Clear
              </button>
              <button
                onClick={handleAnalyze}
                disabled={loading}
                className="flex items-center gap-2 bg-[#8C3A27] hover:bg-[#7A3121] disabled:opacity-60 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md font-medium transition-colors shadow-sm text-sm"
                data-testid="analyze-button"
              >
                {loading ? (
                  <>
                    <span className="tc-pulse" data-testid="analyze-stage">
                      {stage === "validating" ? "Validating… (2/2)" : "Drafting codes… (1/2)"}
                    </span>
                  </>
                ) : (
                  <>
                    <Sparkle size={15} weight="fill" /> Analyze
                  </>
                )}
              </button>
            </footer>
          </section>

          {/* Coding outputs panel */}
          <section className="md:col-span-9 h-full grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 min-h-0">
            <CodeColumn
              title="Injuries"
              subtitle="ICD-10-CM · S00-T88"
              icon={Bandaids}
              items={result.injuries}
              kind="injury"
              loading={loading}
              accepted={accepted.injuries}
              rejected={rejected.injuries}
              onAccept={(idx) => toggleAccept("injuries", idx)}
              onReject={(idx) => toggleReject("injuries", idx)}
              onHover={setHoverEvidence}
              onLeave={() => setHoverEvidence("")}
              favoritesIndex={favoritesIndex}
              onToggleFavorite={handleToggleFavorite}
              onToggleOverride={(item) => handleToggleOverride("injuries", item, false)}
              testId="column-injuries"
            />
            <CodeColumn
              title="Procedures"
              subtitle="ICD-10-PCS · 7-char"
              icon={Knife}
              items={result.procedures}
              kind="procedure"
              loading={loading}
              accepted={accepted.procedures}
              rejected={rejected.procedures}
              onAccept={(idx) => toggleAccept("procedures", idx)}
              onReject={(idx) => toggleReject("procedures", idx)}
              onHover={setHoverEvidence}
              onLeave={() => setHoverEvidence("")}
              favoritesIndex={favoritesIndex}
              onToggleFavorite={handleToggleFavorite}
              onToggleOverride={(item) => handleToggleOverride("procedures", item, false)}
              testId="column-procedures"
            />
            <CodeColumn
              title="External causes"
              subtitle="Place · Mechanism · Activity"
              icon={MapPinLine}
              items={result.external_causes}
              kind="external"
              loading={loading}
              accepted={accepted.external_causes}
              rejected={rejected.external_causes}
              onAccept={(idx) => toggleAccept("external_causes", idx)}
              onReject={(idx) => toggleReject("external_causes", idx)}
              onHover={setHoverEvidence}
              onLeave={() => setHoverEvidence("")}
              favoritesIndex={favoritesIndex}
              onToggleFavorite={handleToggleFavorite}
              onToggleOverride={(item) => handleToggleOverride("external_causes", item, false)}
              testId="column-external"
            />
            <CodeColumn
              title="AIS 2015"
              subtitle="AAAM dictionary · trauma only"
              icon={Ruler}
              items={aisInjuries}
              kind="ais"
              loading={loading}
              accepted={accepted.ais}
              rejected={rejected.ais}
              onAccept={(idx) => toggleAccept("ais", idx)}
              onReject={(idx) => toggleReject("ais", idx)}
              onHover={setHoverEvidence}
              onLeave={() => setHoverEvidence("")}
              favoritesIndex={favoritesIndex}
              onToggleFavorite={handleToggleFavorite}
              onToggleOverride={(item) => handleToggleOverride("injuries", item, true)}
              testId="column-ais"
            />
          </section>
        </div>

        {loading && <ValidationBanner stage={stage} />}
        {result.validated && !loading && <ValidationBanner result={result} />}

        {(result.injuries?.length || 0) > 0 && !loading && (
          <IssSummary stats={issStats} />
        )}

        {/* Summary row */}
        {result.summary && !loading && (
          <div
            className="bg-[#F9EDE6]/60 border border-[#8C3A27]/20 rounded-md px-4 py-2.5 text-sm text-[#2C332A] leading-relaxed"
            data-testid="coding-summary"
          >
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#8C3A27] font-medium mr-2">
              Coder summary
            </span>
            {result.summary}
          </div>
        )}
      </main>
    </div>
  );
}
